import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { buildArtifactDownloadFilename } from './artifactDownloadFilename.js';
import { MessageArtifactCardView, SavedArtifactCardView } from './MessageArtifactCardView.jsx';
import { resolveArtifactSkillIcon } from '../../capabilities/capabilityIcons.jsx';

const ARTIFACT_CARD_VIEWS = new Map([['committed_artifact', SavedArtifactCardView]]);

function resolveString(value) {
  return String(value ?? '').trim();
}

const ARTIFACT_TYPE_LABELS = Object.freeze({
  analysis_report: '分析报告',
  requirement_analysis: '分析报告',
  requirements_document: '需求文档',
  rfx_strategy: '采购方案',
  sourcing_analysis: '寻源分析',
  source_material_preview: '项目资料',
});

const ARTIFACT_TAB_LABELS = Object.freeze({
  analysis: '分析报告',
  requirement: '需求文档',
  rfx: '采购方案',
  sourcing: '寻源分析',
});

function resolveCardTitle(item) {
  const targetTab = resolveString(item?.target_tab);
  const artifactType = resolveString(item?.artifact_type).toLowerCase();
  const label = ARTIFACT_TYPE_LABELS[artifactType] || ARTIFACT_TAB_LABELS[targetTab];
  if (label) {
    return label;
  }
  return '需求文档';
}

function resolveStatusMeta(statusValue) {
  const status = resolveString(statusValue).toLowerCase();
  if (['failed', 'error'].includes(status)) {
    return { color: 'error', label: '失败' };
  }
  if (['stale', 'outdated'].includes(status)) {
    return { color: 'warning', label: '需更新' };
  }
  if (['draft', 'editing'].includes(status)) {
    return { color: 'processing', label: '草稿' };
  }
  if (['accepted', 'completed', 'ready', 'generated', 'passed', 'projected'].includes(status)) {
    return { color: 'success', label: '已生成' };
  }
  if (status) {
    return { color: 'default', label: statusValue };
  }
  return { color: 'success', label: '已生成' };
}

function parseMarkdownTableCells(line) {
  if (!String(line ?? '').includes('|')) {
    return [];
  }
  return String(line ?? '')
    .split('|')
    .map((cell) => cell.trim())
    .filter(Boolean);
}

function isMarkdownTableSeparator(line) {
  const cells = parseMarkdownTableCells(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function markdownTablePreview(line) {
  const cells = parseMarkdownTableCells(line);
  if (cells.length === 2) {
    return `${cells[0]}：${cells[1]}`;
  }
  return cells.join(' · ');
}

function resolvePreview(content) {
  const rawLines = String(content ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !/^[-|:]+$/.test(line));
  const lines = rawLines
    .map((line, index) => {
      if (/^#{1,6}\s+\S/.test(line) || isMarkdownTableSeparator(line)) {
        return '';
      }
      if (parseMarkdownTableCells(line).length > 0) {
        return isMarkdownTableSeparator(rawLines[index + 1])
          ? ''
          : markdownTablePreview(line);
      }
      return line;
    })
    .filter(Boolean);
  const bodyLine = lines.find((line) => !/^#{1,6}\s+\S/.test(line)) || lines[0] || '';
  return bodyLine
    .replace(/^#{1,6}\s*/, '')
    .replace(/\s+/g, ' ')
    .slice(0, 108) || '已生成可打开的文档快照。';
}

function resolveArtifactDocument(item) {
  const title = resolveCardTitle(item);
  const rawArtifactTitle = resolveString(item?.title);
  const content = String(item?.content ?? '');
  return {
    artifactTitle: rawArtifactTitle || title,
    content,
    hasContent: Boolean(content.trim()),
    showArtifactTitle: Boolean(rawArtifactTitle && rawArtifactTitle !== title),
    statusMeta: resolveStatusMeta(item?.status),
    title,
  };
}

function resolveArtifactCapabilities(item, hasContent) {
  return {
    canDownload: item?.can_download !== false && hasContent,
    canOpen: item?.can_open !== false && Boolean(resolveString(item?.artifact_id)),
  };
}

function resolveAccentTheme(themeTokens) {
  const tokens = themeTokens ?? {};
  return {
    accentBorder: tokens.bubbleAssistantBorder || tokens.surfaceBorderActive || '#dbe7ff',
    accentColor: tokens.bubbleUserBg || tokens.surfaceBorderActive || '#315db6',
    accentSurface: tokens.surfaceBgActive || '#f5f8ff',
  };
}

function resolveCardTheme(themeTokens) {
  const tokens = themeTokens ?? {};
  return {
    assistantBackground: tokens.bubbleAssistantBg || '#fff',
    assistantBorder: tokens.bubbleAssistantBorder || '#d9e2ef',
    textPrimary: tokens.textPrimary,
    textSecondary: tokens.textSecondary,
  };
}

export async function copyText(text) {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  let copied = false;
  try {
    if (clipboard?.writeText) {
      await clipboard.writeText(text);
      copied = true;
    }
  } catch {
    copied = false;
  }
  if (copied || typeof document === 'undefined') {
    return copied;
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    copied = document.execCommand('copy');
    document.body.removeChild(textarea);
  } catch {
    copied = false;
  }
  return copied;
}

function downloadTextFile({ filename, text }) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const blob = new Blob([String(text ?? '')], { type: 'text/markdown;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function MessageArtifactCard({ item, onAction, skillProjection, themeTokens }) {
  const [copySuccess, setCopySuccess] = useState(false);
  const documentMeta = resolveArtifactDocument(item);
  const { artifactTitle, content, hasContent, showArtifactTitle, statusMeta, title } = documentMeta;
  const { canDownload, canOpen } = resolveArtifactCapabilities(item, hasContent);
  const accentTheme = resolveAccentTheme(themeTokens);
  const cardTheme = resolveCardTheme(themeTokens);
  const filename = useMemo(() => buildArtifactDownloadFilename({
    content,
    statusLabel: statusMeta.label,
    title: artifactTitle,
    typeLabel: title,
  }), [artifactTitle, content, statusMeta.label, title]);
  const preview = useMemo(() => resolvePreview(content), [content]);

  const handleOpen = useCallback(() => {
    if (!canOpen) {
      return;
    }
    onAction?.(item?.action || {
      type: 'open_artifact_document',
      artifact_id: item?.artifact_id,
      active_tab: item?.target_tab,
    });
  }, [canOpen, item, onAction]);

  const handleCopy = useCallback(async () => {
    if (!hasContent) {
      message.info('当前内容为空，无法复制');
      return;
    }
    const copied = await copyText(content);
    if (!copied) {
      message.error('复制失败');
      return;
    }
    message.success('已复制');
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1200);
  }, [content, hasContent]);

  const handleDownload = useCallback(() => {
    if (!canDownload) {
      return;
    }
    downloadTextFile({ filename, text: content });
  }, [canDownload, content, filename]);

  const View = ARTIFACT_CARD_VIEWS.get(item?.presentation_kind) ?? MessageArtifactCardView;
  return (
    <View
      accentTheme={accentTheme}
      artifactTitle={artifactTitle}
      canDownload={canDownload}
      canOpen={canOpen}
      cardTheme={cardTheme}
      copySuccess={copySuccess}
      hasContent={hasContent}
      onCopy={handleCopy}
      onDownload={handleDownload}
      onOpen={handleOpen}
      preview={preview}
      showArtifactTitle={showArtifactTitle}
      statusMeta={statusMeta}
      title={title}
      icon={resolveArtifactSkillIcon(item?.artifact_type, skillProjection)}
    />
  );
}

export default MessageArtifactCard;
